import React, { useMemo } from 'react';
import useToggle from '@/hooks/useToggle';
import { TextFieldProps } from '@mui/material/TextField';

import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import FloatInput from '@/components/mui/Form/TextField/FloatInput';
import PhoneInput from '@/components/mui/Form/TextField/PhoneInput';
import IntegerInput from '@/components/mui/Form/TextField/IntegerInput';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

type CustomInputType = 'float' | 'tel' | 'int';

interface TextFieldBaseProps extends Omit<TextFieldProps, 'type'> {
  type?: TextFieldProps['type'] | CustomInputType;
  // added to the input slot, so it keeps the type's input component (a slotProps.input would replace it)
  startAdornment?: React.ReactNode;
}

const TextFieldBase = React.forwardRef<HTMLDivElement, TextFieldBaseProps>(
  ({ type, slotProps = {}, startAdornment, ...rest }, ref) => {
    const [isRevealedPassword, toggleRevealedPassword] = useToggle();

    const passwordEye = useMemo(
      () => ({
        endAdornment: (
          <InputAdornment position="end">
            <IconButton aria-label="toggle password visibility" onClick={toggleRevealedPassword}>
              {isRevealedPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      }),
      [isRevealedPassword, toggleRevealedPassword],
    );

    return (
      <TextField
        ref={ref}
        type={isRevealedPassword ? 'text' : type}
        slotProps={{
          input: {
            ...(type === 'password' && passwordEye),
            ...(type === 'float' && { inputComponent: FloatInput }),
            ...(type === 'tel' && { inputComponent: PhoneInput }),
            ...(type === 'int' && { inputComponent: IntegerInput }),
            ...(startAdornment && { startAdornment }),
            ...(slotProps.input || {}),
          },

          ...slotProps,
        }}
        {...rest}
      />
    );
  },
);

TextFieldBase.displayName = 'TextFieldBase';

export default TextFieldBase;
